import Fuse from 'fuse.js';
import { v4 as uuid } from 'uuid';

import { Plate } from 'client/app/apps/disco-prototype/dataStore/dexieStore';
import {
  applyValueUpdate,
  Table,
  Value,
  ValueUpdate,
} from 'client/app/apps/disco-prototype/state/table';
import { isDefined } from 'common/lib/data';

export type LiquidSetId = string;

export type LiquidSet = {
  name: string;
  id: LiquidSetId;
  table: Table;
  plate?: Plate;
  liquidProps: {
    name: Value;
    volume: Value;
    well: Value;
    plateName: Value;
    composition: Component[];
  };
};

export const getDefaultLiquidSet = (table: Table): LiquidSet => {
  return {
    name: table.name,
    id: uuid(),
    table,
    liquidProps: {
      name: {
        selectedMode: 'const',
        value: 'Liquid',
      },
      volume: {
        selectedMode: 'const',
        value: '0',
      },
      well: {
        selectedMode: 'column',
        value: '',
      },
      plateName: {
        selectedMode: 'column',
        value: '',
      },
      composition: [],
    },
  };
};

export type LiquidSetUpdate = {
  name?: string;
  liquidProps?: {
    name?: ValueUpdate;
    volume?: ValueUpdate;
    well?: ValueUpdate;
    plateName?: ValueUpdate;
    composition?: ComponentUpdate;
  };
};

export const applyLiquidSetUpdate = (liquidSet: LiquidSet, update?: LiquidSetUpdate) => {
  if (!update) return;
  liquidSet.name = update.name ?? liquidSet.name;
  if (!update.liquidProps) return;
  applyValueUpdate(liquidSet.liquidProps.name, update.liquidProps.name);
  applyValueUpdate(liquidSet.liquidProps.volume, update.liquidProps.volume);
  applyValueUpdate(liquidSet.liquidProps.well, update.liquidProps.well);
  applyValueUpdate(liquidSet.liquidProps.plateName, update.liquidProps.plateName);
  liquidSet.liquidProps.composition = applyComponentUpdate(
    liquidSet.liquidProps.composition,
    update.liquidProps.composition,
  );
};

type ComponentId = string;

export type Component = {
  id: ComponentId;
  name: Value; // for the demo hardcode as constant in makeMixure Action
  concentration: Value;
};

export const getDefaultComponent = (name: string): Component => {
  return {
    id: uuid(),
    name: {
      selectedMode: 'const',
      value: name,
    },
    concentration: {
      selectedMode: 'const',
      value: '0',
    },
  };
};

export type ComponentUpdate =
  | DeleteComponent
  | CreateComponent
  | UpdateComponent
  | AutoComponents;

type CreateComponent = {
  type: 'create';
  name: string;
};

type UpdateComponent = {
  type: 'update';
  toUpdate: ComponentId;
  name?: ValueUpdate;
  concentration?: ValueUpdate;
};

type DeleteComponent = {
  type: 'delete';
  toDelete: ComponentId;
};

type AutoComponents = {
  type: 'auto';
};

export const applyComponentUpdate = (
  components: Component[],
  update?: ComponentUpdate,
  source?: LiquidSet,
  target?: LiquidSet,
): Component[] => {
  if (!update) {
    return components;
  }
  switch (update.type) {
    case 'create': {
      const newComponent = getDefaultComponent(update.name);
      components.push(newComponent);
      return components;
    }
    case 'update': {
      const toUpdate = components.find(c => c.id === update.toUpdate);
      if (toUpdate) {
        applyValueUpdate(toUpdate.name, update.name);
        applyValueUpdate(toUpdate.concentration, update.concentration);
      }
      return components;
    }
    case 'delete': {
      return components.filter(c => c.id !== update.toDelete);
    }
    case 'auto': {
      if (source && target) {
        return automaticComposition(source, target);
      }
      return components;
    }
    default: {
      const _: never = update;
      console.error(`unexpected update ${_}`);
      return components;
    }
  }
};

const automaticComposition = (source: LiquidSet, target: LiquidSet): Component[] => {
  // use Fuse for fuzzy matching.
  // setting the threshold a little lower than the default means we don't return bad matches
  const fuse = new Fuse(target.table.columnNames, { threshold: 0.3 });

  // assuming components are all specified with constant names
  const componentNames =
    source?.liquidProps.composition.map(c => c.name.value).filter(isDefined) ?? [];

  return componentNames
    .map(cName => {
      // try to find a matching column name in the target table
      // if we don't find a close match, fall back to a constant value
      const bestMatch = fuse.search(cName)[0];
      const concentration: Value = bestMatch
        ? {
            selectedMode: 'column' as 'column',
            selectedColumnName: bestMatch.item,
          }
        : {
            selectedMode: 'const',
            value: '1',
          };
      return {
        id: uuid(),
        name: { selectedMode: 'const' as 'const', value: cName },
        concentration,
      };
    })
    .filter(isDefined);
};
